/**
 * Story 033 — TanStack Query hooks for the three defect-catalogue
 * endpoints + a side-effect bridge that hydrates the Zustand store.
 *
 * Cache lifetimes per Story 033 §"Scope · In scope":
 *   - staleTime: 5 minutes (catalogues rarely change in-session)
 *   - gcTime: 10 minutes
 *   - refetchOnWindowFocus: true (AC10)
 *
 * The retry rule (AC5) — silent retry once on 5xx then surface a toast
 * — leans on TanStack Query's `retry: 1` and a `meta.failureToast`
 * snitched by the query client's `onError` hook (already wired in
 * `apps/web` for other queries that opt-in via the same meta key).
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { useCataloguesStore } from "./cataloguesStore.js";
import type {
  GetDefectCatalogueResponse,
  GetDefectCataloguesResponse,
  GetDefectCatalogueFavouritesResponse,
  ToggleFavouriteBody,
} from "@aeriosiq/types";

export const catalogueKeys = {
  all: ["catalogues"] as const,
  list: () => [...catalogueKeys.all, "list"] as const,
  detail: (catalogueId: string) =>
    [...catalogueKeys.all, "detail", catalogueId] as const,
  favourites: () => [...catalogueKeys.all, "favourites"] as const,
};

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

export function useDefectCataloguesQuery() {
  const api = useApi();
  const setAvailableCatalogues = useCataloguesStore(
    (s) => s.setAvailableCatalogues,
  );
  const query = useQuery<GetDefectCataloguesResponse>({
    queryKey: catalogueKeys.list(),
    queryFn: async () => {
      const res =
        await api.get<GetDefectCataloguesResponse>("/defect-catalogues");
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1, // AC5 — silent retry once
    refetchOnWindowFocus: true, // AC10
    meta: { failureToast: "Catalogues unavailable — contact admin" },
  });
  // Bridge the query data into the Zustand store so non-React consumers
  // (the test-bridge below, the popover, the drawer) share one cache.
  useEffect(() => {
    if (query.data) {
      setAvailableCatalogues(query.data.items);
    }
    if (query.isError) {
      // AC5 — leave the store empty on terminal failure.
      setAvailableCatalogues([]);
    }
  }, [query.data, query.isError, setAvailableCatalogues]);
  return query;
}

export function useDefectCatalogueQuery(catalogueId: string | null) {
  const api = useApi();
  const setEntries = useCataloguesStore((s) => s.setEntries);
  const query = useQuery<GetDefectCatalogueResponse>({
    queryKey: catalogueKeys.detail(catalogueId ?? ""),
    enabled: catalogueId !== null && catalogueId.length > 0,
    queryFn: async () => {
      // queryFn only runs when `enabled` is true (catalogueId !== null).
      const safeId = catalogueId ?? "";
      const res = await api.get<GetDefectCatalogueResponse>(
        `/defect-catalogues/${encodeURIComponent(safeId)}`,
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Catalogue entries unavailable" },
  });
  useEffect(() => {
    if (query.data) {
      setEntries(query.data.catalogueId, query.data.entries);
    }
  }, [query.data, setEntries]);
  return query;
}

export function useFavouritesQuery() {
  const api = useApi();
  const setFavourites = useCataloguesStore((s) => s.setFavourites);
  const query = useQuery<GetDefectCatalogueFavouritesResponse>({
    queryKey: catalogueKeys.favourites(),
    queryFn: async () => {
      const res = await api.get<GetDefectCatalogueFavouritesResponse>(
        "/users/me/preferences/defect-catalogue-favourites",
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Favourites unavailable" },
  });
  useEffect(() => {
    if (query.data) {
      setFavourites(query.data.favouriteCatalogueEntries);
    }
  }, [query.data, setFavourites]);
  return query;
}

interface ToggleFavouriteContext {
  previous: Record<string, string[]>;
}

/**
 * Optimistic toggle (AC7). On mutate we flip the entry in the store; on
 * error we roll back to the previous snapshot. The server response also
 * carries the full post-toggle state, so onSettled we sync the store
 * back to server truth.
 */
export function useToggleFavouriteMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    GetDefectCatalogueFavouritesResponse,
    Error & { errorId?: string },
    ToggleFavouriteBody,
    ToggleFavouriteContext
  >({
    mutationFn: async (body) => {
      const res = await api.post<GetDefectCatalogueFavouritesResponse>(
        "/users/me/preferences/defect-catalogue-favourites",
        body,
      );
      return res.data;
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: catalogueKeys.favourites() });
      const previous = {
        ...useCataloguesStore.getState().favouriteCatalogueEntries,
      };
      const next = { ...previous };
      const arr = next[body.catalogueId] ?? [];
      next[body.catalogueId] = body.favourite
        ? Array.from(new Set([...arr, body.entryId]))
        : arr.filter((e) => e !== body.entryId);
      useCataloguesStore.getState().setFavourites(next);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        useCataloguesStore.getState().setFavourites(ctx.previous);
      }
    },
    onSuccess: (data) => {
      useCataloguesStore
        .getState()
        .setFavourites(data.favouriteCatalogueEntries);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: catalogueKeys.favourites() });
    },
  });
}
